/**
 * The project side nav's up/down bars around the module list (StickySidebar). Two layouts, chosen by width:
 *
 * - "pair" (below md, phones): both bars sit in the flow, one above and one below the list, so they never
 *   cover a row. Whether they are there is decided from two numbers the bars cannot change: the height of
 *   the list's content and the height of the column that holds bars + list. Mounting the bars shrinks the
 *   list's own box, never its content or the column, so the decision cannot flip back. (Asking "does the
 *   list overflow its own box" instead would oscillate: the bars take height out of that box.) At either
 *   end of the scroll range a bar stays mounted but disabled, so scrolling never moves the rows either.
 *
 * - "classic" (md and up, the desktop look): a bar only for a direction the list can scroll, so at the top
 *   of the list there is no up bar and nothing is pushed down. The up bar overlays the list's top edge (it
 *   only appears once the list is scrolled, so it never moves the rows). The down bar sits in the flow at
 *   the column's bottom edge: it paints exactly where an overlay would, but the last row is cut by the
 *   list's edge instead of lying under the bar, where a tap on its visible part hit the bar (the
 *   desktop-1366 Contracts overlap in the mobile-layout baseline). Its decision reads the column's height,
 *   which the bar is part of, so mounting it cannot flip it back.
 */

export type ScrollBarMode = "pair" | "classic";

export interface ScrollBarMeasure {
  /** The list's `scrollHeight`: its content, or its own box when that is taller. */
  contentHeight: number;
  /** `clientHeight` of the column that holds the bars and the list. */
  wrapperHeight: number;
  /** The list's `scrollTop`. */
  scrollTop: number;
  /** The list's `clientHeight` (the column minus the in-flow bars while they are shown). */
  clientHeight: number;
}

export interface ScrollBarView {
  /** Operable: there is content that way. A bar that is shown but not enabled is greyed out. */
  enabled: boolean;
  /** Positioned over the list's edge instead of taking height from it. */
  overlay: boolean;
}

export interface ScrollBars {
  /** `null`: no bar is rendered at that edge. */
  up: ScrollBarView | null;
  down: ScrollBarView | null;
}

/**
 * Heights come back rounded while `scrollTop` is fractional; the same 1px tolerance as everywhere else in
 * the repo that asks this (gridCellTooltip.ts, NotificationCard.tsx).
 */
export const SCROLL_TOLERANCE_PX = 1;

export function decideScrollBars(
  { contentHeight, wrapperHeight, scrollTop, clientHeight }: ScrollBarMeasure,
  mode: ScrollBarMode,
): ScrollBars {
  if (mode === "classic") {
    return {
      up: scrollTop > 0 ? { enabled: true, overlay: true } : null,
      down: contentHeight - wrapperHeight - scrollTop > SCROLL_TOLERANCE_PX ? { enabled: true, overlay: false } : null,
    };
  }
  if (contentHeight - wrapperHeight <= SCROLL_TOLERANCE_PX) return { up: null, down: null };
  return {
    up: { enabled: scrollTop > 0, overlay: false },
    down: { enabled: contentHeight - clientHeight - scrollTop > SCROLL_TOLERANCE_PX, overlay: false },
  };
}

/** Same bars, same states: lets the sidebar skip a re-render on a scroll event that changes nothing. */
export function sameScrollBars(a: ScrollBars, b: ScrollBars): boolean {
  const sameView = (x: ScrollBarView | null, y: ScrollBarView | null) =>
    x === null || y === null ? x === y : x.enabled === y.enabled && x.overlay === y.overlay;
  return sameView(a.up, b.up) && sameView(a.down, b.down);
}
